import React, { useEffect, useMemo, useState } from "react";
import { getReglements } from "../../services/achatsServices";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import {
  FiRefreshCw,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
} from "react-icons/fi";

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const formatDate = (d) =>
  new Date(d).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const StatusPill = ({ children, variant = "neutral" }) => {
  const map = {
    success: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
    warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
    danger: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
    neutral: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200",
  };
  return (
    <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${map[variant] || map.neutral}`}>
      {children}
    </span>
  );
};

export default function PaymentsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // UI state
  const [q, setQ] = useState("");
  const [statutFilter, setStatutFilter] = useState("ALL"); // ALL | Réglé | Non Réglé
  const [paymentFilter, setPaymentFilter] = useState("ALL"); // ALL | paid | unpaid
  const [sortBy, setSortBy] = useState({ key: "date_achat", dir: "desc" }); // asc|desc
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const load = async () => {
    try {
      setErr("");
      setLoading(true);
      const data = await getReglements(); // [{..., User:{nom, prenom, email, role_id}}]
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErr("Erreur lors du chargement des paiements.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Derived data (filter + search)
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      const statutOk = statutFilter === "ALL" ? true : r.statut_reglement === statutFilter;
      const paymentOk =
        paymentFilter === "ALL"
          ? true
          : paymentFilter === "paid"
          ? (r.payment_status || "").toLowerCase() === "paid"
          : (r.payment_status || "").toLowerCase() !== "paid";

      const user = r.User || {};
      const text =
        [
          r.formule,
          r.statut_reglement,
          r.payment_status,
          user.email,
          user.nom,
          user.prenom,
          getRoleFromRoleId(user.role_id),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase() || "";

      const match = needle ? text.includes(needle) : true;
      return statutOk && paymentOk && match;
    });
  }, [rows, q, statutFilter, paymentFilter]);

  // Sorting
  const sorted = useMemo(() => {
    const { key, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va =
        key === "prix_total"
          ? Number(a.prix_total)
          : key === "date_achat"
          ? new Date(a.date_achat).getTime()
          : key === "formule"
          ? (a.formule || "").toLowerCase()
          : key === "user"
          ? `${a.User?.prenom || ""} ${a.User?.nom || ""}`.toLowerCase()
          : a[key];

      const vb =
        key === "prix_total"
          ? Number(b.prix_total)
          : key === "date_achat"
          ? new Date(b.date_achat).getTime()
          : key === "formule"
          ? (b.formule || "").toLowerCase()
          : key === "user"
          ? `${b.User?.prenom || ""} ${b.User?.nom || ""}`.toLowerCase()
          : b[key];

      if (va < vb) return -1 * mul;
      if (va > vb) return 1 * mul;
      return 0;
    });
  }, [filtered, sortBy]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageRows = sorted.slice(start, start + pageSize);

  useEffect(() => {
    // reset sur changement de filtres/recherche
    setPage(1);
  }, [q, statutFilter, paymentFilter, pageSize]);

  const sumVisible = useMemo(
    () => pageRows.reduce((acc, r) => acc + (Number(r.prix_total) || 0), 0),
    [pageRows]
  );

  const setSort = (key) => {
    setSortBy((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const SortIcon = ({ col }) =>
    sortBy.key === col ? (sortBy.dir === "asc" ? <FiArrowUp /> : <FiArrowDown />) : null;

  return (
    <section className="w-full font-montserrat bg-slate-200 p-4 rounded-xl shadow-lg border border-gray-400">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="text-lg font-light uppercase">Paiements</h3>

        <div className="flex items-center gap-2 text-sm">
          <div className="relative">
            <FiSearch className="absolute left-2 top-2.5 opacity-60" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (nom, email, formule, rôle...)"
              className="pl-8 pr-3 py-2 rounded border dark:border-gray-700 bg-white dark:bg-gray-800"
            />
          </div>

          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
            className="py-2 px-2 rounded border dark:border-gray-700 bg-white dark:bg-gray-800"
            title="Filtrer par statut de règlement"
          >
            <option value="ALL">Tous statuts</option>
            <option value="Réglé">Réglé</option>
            <option value="Non Réglé">Non Réglé</option>
          </select>

          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="py-2 px-2 rounded border dark:border-gray-700 bg-white dark:bg-gray-800"
            title="Filtrer par Stripe status"
          >
            <option value="ALL">Tous paiements</option>
            <option value="paid">Stripe: paid</option>
            <option value="unpaid">Stripe: non-paid</option>
          </select>

          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="py-2 px-2 rounded border dark:border-gray-700 bg-white dark:bg-gray-800"
            title="Lignes par page"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>

          <button
            type="button"
            onClick={load}
            className="btn btn-sm btn-outline"
            title="Recharger"
            aria-label="Recharger"
          >
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="rounded bg-gray-100 dark:bg-gray-800 p-4 animate-pulse">Chargement…</div>
      ) : err ? (
        <div className="p-4 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
          {err}
        </div>
      ) : sorted.length === 0 ? (
        <div className="p-4 rounded border border-dashed dark:border-gray-700">
          Aucun paiement trouvé.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-gray-50 dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-700 dark:text-gray-200">
                  <th
                    className="px-3 py-2 cursor-pointer select-none"
                    onClick={() => setSort("date_achat")}
                    title="Trier par date"
                  >
                    <div className="flex items-center gap-2">
                      Date
                      <SortIcon col="date_achat" />
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 cursor-pointer select-none"
                    onClick={() => setSort("user")}
                    title="Trier par utilisateur"
                  >
                    <div className="flex items-center gap-2">
                      Utilisateur
                      <SortIcon col="user" />
                    </div>
                  </th>
                  <th className="px-3 py-2">Rôle</th>
                  <th
                    className="px-3 py-2 cursor-pointer select-none"
                    onClick={() => setSort("formule")}
                    title="Trier par formule"
                  >
                    <div className="flex items-center gap-2">
                      Formule
                      <SortIcon col="formule" />
                    </div>
                  </th>
                  <th
                    className="px-3 py-2 cursor-pointer select-none"
                    onClick={() => setSort("prix_total")}
                    title="Trier par montant"
                  >
                    <div className="flex items-center gap-2">
                      Montant
                      <SortIcon col="prix_total" />
                    </div>
                  </th>
                  <th className="px-3 py-2">Statut règlement</th>
                  <th className="px-3 py-2">Stripe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.map((r) => {
                  const u = r.User || {};
                  const fullName = [u.prenom, u.nom].filter(Boolean).join(" ") || "-";
                  const role = getRoleFromRoleId(u.role_id);
                  const isPaid = (r.payment_status || "").toLowerCase() === "paid";
                  const isRegle = r.statut_reglement === "Réglé";
                  return (
                    <tr key={r.id} className="hover:bg-gray-100 dark:hover:bg-gray-700/60">
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDate(r.date_achat)}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{fullName}</div>
                        <div className="text-xs opacity-70">{u.email || "-"}</div>
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill variant="neutral">{role}</StatusPill>
                      </td>
                      <td className="px-3 py-2">{r.formule}</td>
                      <td className="px-3 py-2 font-semibold">{formatEUR(r.prix_total)}</td>
                      <td className="px-3 py-2">
                        <StatusPill variant={isRegle ? "success" : "warning"}>
                          {r.statut_reglement}
                        </StatusPill>
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill variant={isPaid ? "success" : "danger"}>
                          {r.payment_status || "—"}
                        </StatusPill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer: pagination + résumé */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="opacity-80">
              Affichés&nbsp;
              <b>
                {start + 1}–{Math.min(start + pageSize, sorted.length)}
              </b>
              &nbsp;sur <b>{sorted.length}</b> | Somme page : <b>{formatEUR(sumVisible)}</b>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="btn btn-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
              </button>
              <span>
                Page <b>{currentPage}</b>/<b>{totalPages}</b>
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
